import { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSupabaseAuth } from "./useSupabaseAuth";
import { useToast } from "./use-toast";
import { useTenant } from "./useTenant";
import { googleOAuthCoordinator } from "@/lib/googleOAuthCoordinator";

const GOOGLE_SCOPES = [
  "https://www.googleapis.com/auth/calendar",
  "https://www.googleapis.com/auth/calendar.events",
];

interface UseGoogleOAuthReturn {
  syncStatus: 'idle' | 'syncing' | 'success' | 'error';
  calendarId: string | null;
  retrySyncNow: () => Promise<boolean>;
}

export const useGoogleOAuth = (onSyncComplete?: () => void): UseGoogleOAuthReturn => {
  const { user, session } = useSupabaseAuth();
  const { toast } = useToast();
  const { tenant, isGlobalAdmin } = useTenant();
  const lastSyncSignature = useRef<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'success' | 'error'>('idle');
  const [calendarId, setCalendarId] = useState<string | null>(null);

  const syncTokens = useCallback(async (accessToken: string, refreshToken: string, expiresIn: number): Promise<boolean> => {
    if (!user?.id) {
      console.log("❌ No user ID available for sync");
      return false;
    }

    console.log("🔄 useGoogleOAuth: Requesting token sync...", {
      expiresIn,
      scopes: GOOGLE_SCOPES,
      userId: user.id,
    });

    // Use the global coordinator to prevent duplicate syncs
    return googleOAuthCoordinator.coordinateSync(user.id, async () => {
      setSyncStatus('syncing');

    try {
      const { data, error } = await supabase.functions.invoke("ef_google_oauth_sync", {
        body: {
          provider: "google",
          access_token: accessToken,
          token_type: session?.token_type ?? "Bearer",
          refresh_token: refreshToken,
          scopes: GOOGLE_SCOPES,
          expires_in: expiresIn,
        },
      });

      if (error) {
        console.error("❌ useGoogleOAuth: Sync failed (non-critical)", error);
        setSyncStatus('error');
        lastSyncSignature.current = null;
        return false;
      }

      console.log("✅ useGoogleOAuth: Sync successful", data);
      
      // Extract calendar ID from response if available
      if (data?.calendar_id) {
        setCalendarId(data.calendar_id);
        console.log("📅 Detected calendar ID:", data.calendar_id);
      }

      setSyncStatus('success');
      lastSyncSignature.current = `${accessToken}:${refreshToken}`;
      
      toast({
        title: "Google Calendar Connected",
        description: data?.calendar_id 
          ? `Connected to: ${data.calendar_id}` 
          : "Your calendar is now connected",
      });

      // Trigger callback for parent components to refresh
      if (onSyncComplete) {
        onSyncComplete();
      }

      return true;
    } catch (error) {
      console.error("💥 useGoogleOAuth: Unexpected error during sync (non-critical)", error);
      setSyncStatus('error');
      lastSyncSignature.current = null;
      return false;
    }
    }); // End of coordinateSync
  }, [user?.id, session?.token_type, toast, onSyncComplete]);

  const retrySyncNow = useCallback(async (): Promise<boolean> => {
    const accessToken = session?.provider_token ?? null;
    const refreshToken = session?.provider_refresh_token ?? null;

    if (!user || !accessToken || !refreshToken) {
      console.log("❌ Cannot retry sync: missing credentials");
      toast({
        title: "Sync Error",
        description: "Missing OAuth credentials. Please reconnect your calendar.",
        variant: "destructive",
      });
      return false;
    }

    // Clear cooldown to allow immediate retry
    googleOAuthCoordinator.clearCooldown(user.id);

    const expiresIn = (() => {
      if (typeof session?.expires_in === "number") {
        return Math.max(session.expires_in, 60);
      }
      if (session?.expires_at) {
        const delta = session.expires_at - Math.floor(Date.now() / 1000);
        return Math.max(delta, 60);
      }
      return 3600;
    })();

    return syncTokens(accessToken, refreshToken, expiresIn);
  }, [session, user, syncTokens, toast]);

  useEffect(() => {
    const accessToken = session?.provider_token ?? null;
    const refreshToken = session?.provider_refresh_token ?? null;

    console.log("🔍 useGoogleOAuth: Session state", {
      hasSession: !!session,
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      provider: session?.user?.app_metadata?.provider,
      userId: user?.id,
    });

    if (!user || !accessToken || !refreshToken) {
      console.log("⏭️ useGoogleOAuth: Missing required data, skipping sync");
      return;
    }

    const tokenSignature = `${accessToken}:${refreshToken}`;
    if (lastSyncSignature.current === tokenSignature) {
      console.log("⏭️ useGoogleOAuth: Token signature unchanged, skipping sync");
      return;
    }

    const expiresIn = (() => {
      if (typeof session?.expires_in === "number") {
        return Math.max(session.expires_in, 60);
      }
      if (session?.expires_at) {
        const delta = session.expires_at - Math.floor(Date.now() / 1000);
        return Math.max(delta, 60);
      }
      return 3600;
    })();

    // Small delay to ensure session is fully established
    const timeoutId = setTimeout(() => {
      syncTokens(accessToken, refreshToken, expiresIn);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [session, user, syncTokens, tenant?.id]);

  return { syncStatus, calendarId, retrySyncNow };
};


